import { formatSlotLabel } from '@musicville/shared';
import { Icon } from '@/components/brand/Icon';
import { LinkButton } from '@/components/ui/Button';
import { MediaFrame } from '@/components/ui/MediaFrame';
import { useAvailability, useSubjects } from '@/lib/queries';
import { useI18n } from '@/lib/i18n';

export function Hero(): React.JSX.Element {
  const { t, locale } = useI18n();
  const { data } = useAvailability({ days: 7 });
  const { data: subjects } = useSubjects();

  const next = data?.days.flatMap((day) => day.slots)[0];

  return (
    <section className="bg-midnight text-on-midnight">
      <div className="mv-container grid items-center gap-16 py-16 md:py-20 lg:grid-cols-[1fr_560px] lg:py-24">
        <div className="flex flex-col items-start gap-7">
          <p className="text-overline font-semibold uppercase text-on-midnight-accent">
            {t('hero.eyebrow')}
          </p>

          <h1 className="max-w-[16ch] font-display text-[clamp(2.75rem,7vw,4.5rem)] font-light leading-[1.06] tracking-[-0.02em]">
            {t('hero.headline')}
          </h1>

          <p className="max-w-[34rem] text-body-lg text-on-midnight-muted">{t('hero.sub')}</p>

          <div className="flex flex-wrap gap-3 pt-2">
            <LinkButton to="/booking/subject" size="lg" trailingIcon="arrow-right">
              {t('nav.bookTrial')}
            </LinkButton>
            <LinkButton to="/teachers" size="lg" variant="secondary">
              {t('hero.exploreTeachers')}
            </LinkButton>
          </div>

          <ul className="flex flex-wrap gap-7 pt-4">
            <TrustItem
              icon="piano"
              label={t('hero.trust.disciplines', subjects ? { count: subjects.length } : undefined)}
            />
            <TrustItem icon="user" label={t('hero.trust.oneToOne')} />
            <TrustItem icon="clock" label={t('hero.trust.trials')} />
          </ul>
        </div>

        <div className="relative">
          <MediaFrame ratio="hero" src="/media/hero/home.png" alt="" className="rounded-md" />

          <div className="absolute bottom-8 left-0 w-[17rem] max-w-[85%] rounded-md bg-surface px-[18px] py-4 shadow-overlay lg:-left-18">
            <p className="flex items-center gap-2 text-caption font-medium text-secondary">
              <Icon name="clock" size={16} />
              {t('hero.nextTrial')}
            </p>
            <p className="mt-2.5 text-h4 font-semibold text-primary">
              {next ? formatSlotLabel(next.startsAt, locale) : t('hero.noTrial')}
            </p>
            <p className="mt-2.5 text-caption text-tertiary">
              {next ? `${capitalise(next.subjectSlug)} with ${next.teacherName}` : ''}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustItem({ icon, label }: { icon: 'piano' | 'user' | 'clock'; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <Icon name={icon} size={16} className="text-on-midnight-muted" />
      <span className="text-caption font-medium text-on-midnight-muted">{label}</span>
    </li>
  );
}

function capitalise(slug: string): string {
  return slug
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
